import {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {
  Button,
  Input,
  Space,
  Spin,
  Table,
  Popconfirm,
  Tag,
  message,
} from 'antd';
import moment from 'moment';

import {CurriculumsService} from './CurriculumsService';
import {CurriculumUI} from './CurriculumTypes';
import Title from 'antd/es/typography/Title';
import Text from 'antd/es/typography/Text';
import {SearchOutlined, DeleteOutlined} from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import {get, isEqual} from 'lodash';

const curriculumsService = new CurriculumsService();

export const CurriculumsTable = () => {
  const [curriculums, setCurriculums] = useState<Array<CurriculumUI> | []>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchedColumn, setSearchedColumn] = useState<any>();
  const [searchText, setSearchText] = useState<any>();
  let searchInput = useRef();

  useEffect(() => {
    curriculumsService
      .getCurriculums()
      .then(resp => {
        setIsLoading(false);
        setCurriculums(resp);
      })
      .catch(error => {
        setIsError(true);
        //console.log('Error ->', error);
      });
  }, []);

  const getColumnSearchProps = (dataIndex: any) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{padding: 8}}>
        <Input
          ref={node => {
            // @ts-ignore
            searchInput = node;
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{marginBottom: 8, display: 'block'}}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{width: 90}}>
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{width: 90}}>
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({closeDropdown: false});
              setSearchedColumn(dataIndex);
              setSearchText(selectedKeys[0]);
            }}>
            Filter
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: any) => (
      <SearchOutlined style={{color: filtered ? '#1890ff' : undefined}} />
    ),
    onFilter: (value: any, record: any) =>
      get(record, dataIndex)
        ? get(record, dataIndex)
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: (visible: any) => {
      if (visible) {
        // @ts-ignore
        setTimeout(() => searchInput.select(), 100);
      }
    },
    render: (text: any) =>
      isEqual(searchedColumn, dataIndex) ? (
        <Highlighter
          highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });
  const handleSearch = (selectedKeys: any, confirm: any, dataIndex: any) => {
    confirm();
    setSearchedColumn(dataIndex);
    setSearchText(selectedKeys[0]);
  };

  const handleReset = (clearFilters: any) => {
    clearFilters();
    setSearchText('');
  };

  const handleDelete = async (id: string) => {
    try {
      setIsLoading(true);
      await curriculumsService.softDeleteNewCurriculum(id);
      message.success('Curriculum deleted successfully');
      // Refresh the table
      const updatedCurriculums = await curriculumsService.getCurriculums();
      setCurriculums(updatedCurriculums);
      setIsLoading(false);
    } catch (error) {
      message.error('Failed to delete curriculum');
      console.error('Delete error:', error);
      setIsLoading(false);
    }
  };

  const columns: any = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      ...getColumnSearchProps('id'),
      sorter: (a: any, b: any) => a.id.length - b.id.length,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (id: string) => (
        <Link to={'/curriculums/' + id + '/edit'}>{id}</Link>
      ),
    },
    {
      title: 'Created at',
      dataIndex: ['createdAt'],
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a: any, b: any) =>
        moment(a.createdAt).unix() - moment(b.createdAt).unix(),
    },
    {
      title: 'name?',
      dataIndex: ['name'],
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a: any, b: any) => a.name - b.name,
      render: (name: string) => <p style={{}}>{name}</p>,
    },
    {
      title: 'Type',
      dataIndex: ['type'],
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: (a: any, b: any) => (a.type || '').localeCompare(b.type || ''),
      render: (type: string) => (
        <Tag color={type === 'BUSINESS' ? 'blue' : 'green'}>
          {type || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Published',
      dataIndex: ['isPublished'],
      render: (isPublished: boolean) => (
        <Tag color={isPublished ? 'green' : 'red'}>
          {isPublished ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Academic',
      dataIndex: ['isAcademic'],
      render: (isAcademic: boolean) => (
        <Tag color={isAcademic ? 'purple' : 'default'}>
          {isAcademic ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'id',
      render: (id: string) => (
        <Space>
          <Link to={'/curriculums/' + id + '/edit'}>
            <Button>Edit</Button>
          </Link>
          {/* <Popconfirm
            title="Are you sure you want to delete this curriculum?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(id)}>
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm> */}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Button size={'large'} style={{float: 'right', margin: 20}}>
        <Link to={'/curriculums/create'}>Create</Link>
      </Button>
      {!isError && !isLoading && (
        <Table
          pagination={{defaultPageSize: 100}}
          columns={columns}
          dataSource={curriculums}
        />
      )}
      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>curriculums fetching error ⚠️</Title>}
    </>
  );
};
